import React, { useState, useEffect } from 'react';
import DatePicker from 'react-datepicker';
import { Box, Typography, Grid } from '@mui/material';
import { useAppSelector } from 'store/hook';
import SquaresDashboardHandler from 'handlers/squaresHandlers/squaresDashboard.handler';
import { ComponentsTreeChart } from './Components/TreeChart';
import { ComponentsPieChart } from './Components/PieChart';
import { ComponentsLineChart } from './Components/LineChart';
import { Base } from 'components/Containers/BaseContainer';
import {
  extractWorkOrderData,
  getAssetsByKey,
  extractSelectedAssets,
  getFirstAttribute,
} from './utils';
import { ThemePalette } from 'mui.theme';
import LoadingDots from 'views/FleetOverview/components/AssetCardsV2/LoadingDots';

export const PartsViewV2 = ({
  enableDarkTheme,
}: {
  enableDarkTheme: boolean;
}) => {
  const squaresHandler = new SquaresDashboardHandler();
  const { dashboard: data } = useAppSelector(
    (state) => state.squaresDashboardReducer
  );
  const { isLoading } = useAppSelector((state) => state.appReducer);
  const formerSelectedDates: any = sessionStorage.getItem(
    'datePickerSelection'
  );
  let initialStartDate = new Date(
    new Date().getTime() - 7 * 24 * 60 * 60 * 1000
  );
  let initialEndDate = new Date();
  if (formerSelectedDates) {
    const dateObjs = JSON.parse(formerSelectedDates);

    initialStartDate = new Date(dateObjs.startDate);
    initialEndDate = new Date(dateObjs.endDate);
  }
  const [selectedStartDate, setStartDate] = useState(initialStartDate);
  const [selectedEndDate, setEndDate] = useState(initialEndDate);

  const handleDateChange = (range: any) => {
    const [startDate, endDate] = range;
    setStartDate(startDate);
    setEndDate(endDate);

    sessionStorage.setItem(
      'datePickerSelection',
      JSON.stringify({
        startDate,
        endDate,
      })
    );
  };
  const [selectedCell, setSelectedCell] = useState<string>('');
  const [selectedAssets, setSelectedAssets] = useState<string[]>([]);

  const handleTreeCellClick = (name: string) => {
    setSelectedCell(name);
    // When new tree cell is selected Clear assets
    if (selectedCell !== name) {
      setSelectedAssets([]);
    }
  };

  const handlePieClick = (data: any) => {
    const name = data.name;
    const updatedClickedCells = [...selectedAssets];
    if (updatedClickedCells.includes(name)) {
      const indexToRemove = updatedClickedCells.indexOf(name);
      updatedClickedCells.splice(indexToRemove, 1);
    } else {
      updatedClickedCells.push(name);
    }
    setSelectedAssets(updatedClickedCells);
  };

  const workOrderData = extractWorkOrderData(data.components);
  const pieChartData = getAssetsByKey(data.components, selectedCell);
  const selectedAssetsData = extractSelectedAssets(data.assets, selectedAssets);

  useEffect(() => {
    if (selectedStartDate && selectedEndDate) {
      setSelectedAssets([]);
      void squaresHandler.getPartsComponentsData(
        selectedStartDate,
        selectedEndDate
      );
    }
  }, [selectedStartDate, selectedEndDate]);

  useEffect(() => {
    const highestKey = getFirstAttribute(data.components);
    setSelectedCell(highestKey);
  }, [data]);

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          textAlign: 'center',
          paddingLeft: '40%',
          paddingTop: '10px',
          paddingBottom: '20px',
        }}
      >
        <Typography
          sx={{
            textAlign: 'center',
            paddingRight: '10px',
            fontSize: '20px',
            color: enableDarkTheme
              ? ThemePalette.typography.white
              : ThemePalette.typography.black,
          }}
        >
          {' '}
          Parts Work Order Cost Totals
        </Typography>
        <DatePicker
          showIcon
          selected={selectedStartDate}
          onChange={handleDateChange}
          startDate={selectedStartDate}
          endDate={selectedEndDate}
          maxDate={new Date(new Date().getTime())}
          selectsRange
        />
      </Box>
      {/* {isLoading ? ( */}
      {isLoading == null ? (
        <></>
      ) : (
        <>
          {workOrderData.length <= 0 ? (
            <Typography
              sx={{
                textAlign: 'center',
                fontSize: '20px',
                paddingTop: '10px',
                color: enableDarkTheme
                  ? ThemePalette.typography.white
                  : ThemePalette.typography.black,
              }}
            >
              Waiting For Assets
              <LoadingDots enableDarkTheme={enableDarkTheme} />{' '}
            </Typography>
          ) : (
            <>
              <Base
                sx={{
                  padding: '20px 20px 40px 20px',
                  backgroundColor: enableDarkTheme
                    ? ThemePalette.dark.toolBarBackground
                    : ThemePalette.light.toolBarBackground,
                }}
              >
                <Box
                  sx={{
                    height: '400px',
                    maxHeight: '400px',
                  }}
                >
                  <ComponentsTreeChart
                    selectedCell={selectedCell}
                    handleCellClick={handleTreeCellClick}
                    data={workOrderData}
                    enableDarkTheme={enableDarkTheme}
                  />
                </Box>
              </Base>

              <Grid
                sx={{
                  display: 'flex',
                  marginLeft: '0px',
                  maxWidth: '100%',
                  paddingBottom: '30px',
                }}
                container
                spacing={2}
              >
                <Grid
                  item
                  lg={12}
                  md={12}
                  sm={12}
                  xs={12}
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-around',
                    marginLeft: '0px',
                    paddingBottom: '30px',
                  }}
                >
                  <Grid item lg={3} md={12} sm={12} xs={12}>
                    <Base
                      sx={{
                        height: '400px',
                        maxHeight: '400px',
                        backgroundColor: enableDarkTheme
                          ? ThemePalette.dark.toolBarBackground
                          : ThemePalette.light.toolBarBackground,
                      }}
                    >
                      <Box
                        sx={{
                          alignItems: 'center',
                          display: 'flex',
                          flexDirection: 'column',
                        }}
                      >
                        <Typography
                          sx={{
                            textAlign: 'center',
                            fontSize: '15px',
                            paddingTop: '30px',
                            color: enableDarkTheme
                              ? ThemePalette.typography.white
                              : ThemePalette.typography.black,
                          }}
                        >
                          {`Asset distribution by total cost for `}
                        </Typography>
                        <Typography
                          sx={{
                            textAlign: 'center',
                            fontSize: '20px',

                            color: enableDarkTheme
                              ? ThemePalette.typography.white
                              : ThemePalette.typography.black,
                          }}
                        >
                          {`${selectedCell}`}
                        </Typography>
                      </Box>
                      <ComponentsPieChart
                        data={pieChartData}
                        handlePieClick={handlePieClick}
                        enableDarkTheme={enableDarkTheme}
                      />
                    </Base>
                  </Grid>
                  <Grid item lg={8} md={12} sm={12} xs={12}>
                    <Base
                      sx={{
                        height: '400px',
                        maxHeight: '400px',
                        backgroundColor: enableDarkTheme
                          ? ThemePalette.dark.toolBarBackground
                          : ThemePalette.light.toolBarBackground,
                      }}
                    >
                      <Box>
                        <Typography
                          sx={{
                            textAlign: 'center',
                            fontSize: '15px',
                            paddingTop: '30px',
                            color: enableDarkTheme
                              ? ThemePalette.typography.white
                              : ThemePalette.typography.black,
                          }}
                        >
                          {`Asset distribution by total cost for `}
                        </Typography>
                        <Typography
                          sx={{
                            textAlign: 'center',
                            fontSize: '20px',

                            color: enableDarkTheme
                              ? ThemePalette.typography.white
                              : ThemePalette.typography.black,
                          }}
                        >
                          {`${selectedCell}`}
                        </Typography>
                      </Box>
                      {selectedAssets.length < 1 ? (
                        <Typography
                          sx={{
                            textAlign: 'center',
                            fontSize: '20px',
                            paddingTop: '10px',
                            color: enableDarkTheme
                              ? ThemePalette.typography.white
                              : ThemePalette.typography.black,
                          }}
                        >
                          Please select an asset from the pie chart.
                        </Typography>
                      ) : (
                        <ComponentsLineChart data={selectedAssetsData} />
                      )}
                    </Base>
                  </Grid>
                </Grid>
              </Grid>
            </>
          )}
        </>
      )}
    </Box>
  );
};
